import type { FireworksEvent } from '@shared/types'

import { OVERLAY_CONFIG } from '@shared/config'
import { useEffectSound } from '@shared/lib/audio'
import { type EffectComponentProps } from '@shared/lib/effect-registry'
import { prefersReducedMotion } from '@shared/lib/waapi'
import { OverlayEffectSlot } from '@shared/ui/OverlayEffectSlot'
import { useEffect, useRef } from 'react'

const DEFAULT_COLORS = [
  '#ff4d6d', '#ff9f1c', '#ffbf69', '#cbf3f0',
  '#2ec4b6', '#a8dadc', '#e9c46a', '#f4a261',
  '#ffffff', '#c77dff', '#48cae4',
]
const INTENSITY = {
  low:    { shells: 3,  particles: 60  },
  medium: { shells: 6,  particles: 90  },
  high:   { shells: 10, particles: 130 },
}

interface Particle { x:number;y:number;vx:number;vy:number;alpha:number;color:string;radius:number;gravity:number;decay:number }
interface Shell    { x:number;y:number;vy:number;color:string;exploded:boolean }

export function FireworksEffect({ event, onDone }: EffectComponentProps<FireworksEvent>) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef    = useRef<number>(0)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  // Воспроизвести звук плагина при старте эффекта
  useEffectSound(event)

  const duration  = event.payload.duration  ?? OVERLAY_CONFIG.FIREWORKS_DEFAULT_DURATION_MS
  const intensity = event.payload.intensity ?? 'medium'
  const colors    = event.payload.colors    ?? DEFAULT_COLORS

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    if (prefersReducedMotion()) { onDoneRef.current(); return }

    const { shells: maxShells, particles: particlesPerShell } = INTENSITY[intensity]
    const shellInterval = (duration / maxShells) * 0.6

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight }
    resize()
    window.addEventListener('resize', resize)

    const shells: Shell[] = []
    const particles: Particle[] = []
    const startTime = performance.now()
    let lastShellTime = 0

    const tick = (now: number) => {
      const elapsed = now - startTime
      ctx.fillStyle = 'rgba(0,0,0,0.15)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      if (elapsed < duration * 0.85 && now - lastShellTime > shellInterval) {
        shells.push({ x: Math.random()*canvas.width, y: canvas.height, vy: -(Math.random()*12+10), color: colors[Math.floor(Math.random()*colors.length)], exploded: false })
        lastShellTime = now
      }
      for (let i = shells.length-1; i >= 0; i--) {
        const s = shells[i]
        if (s.exploded) { shells.splice(i,1); continue }
        s.y += s.vy; s.vy += 0.25
        ctx.beginPath(); ctx.arc(s.x,s.y,3,0,Math.PI*2); ctx.fillStyle=s.color; ctx.fill()
        if (s.vy >= 0) {
          for (let j=0; j<particlesPerShell; j++) {
            const angle=(Math.PI*2*j)/particlesPerShell, speed=Math.random()*4+2
            particles.push({ x:s.x, y:s.y, vx:Math.cos(angle)*speed, vy:Math.sin(angle)*speed, alpha:1, color:s.color, radius:Math.random()*2.5+1, gravity:0.08, decay:Math.random()*0.012+0.012 })
          }
          s.exploded = true
        }
      }
      for (let i = particles.length-1; i >= 0; i--) {
        const p = particles[i]
        p.x+=p.vx; p.y+=p.vy; p.vy+=p.gravity; p.vx*=0.98; p.alpha-=p.decay
        if (p.alpha<=0) { particles.splice(i,1); continue }
        ctx.save(); ctx.globalAlpha=p.alpha; ctx.beginPath(); ctx.arc(p.x,p.y,p.radius,0,Math.PI*2); ctx.fillStyle=p.color; ctx.fill(); ctx.restore()
      }
      if (elapsed < duration || particles.length > 0) rafRef.current = requestAnimationFrame(tick)
      else { ctx.clearRect(0,0,canvas.width,canvas.height); onDoneRef.current() }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { cancelAnimationFrame(rafRef.current); window.removeEventListener('resize', resize) }
  }, [duration, intensity, colors])

  // Фейерворки — всегда на весь экран, без зонирования от backend. animateIn=false —
  // canvas уже сам плавно засеивается частицами салюта — добавочный fade всего
  // контейнера не нужен. z-index выше дефолтного у слота (9000) — салют должен быть
  // над остальными наслаивающимися эффектами — как и раньше (zIndex:9999).
  return (
    <OverlayEffectSlot zone="fullscreen" animateIn={false} style={{ zIndex: 9999 }}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
        aria-hidden="true"
      />
    </OverlayEffectSlot>
  )
}
